/**
 * "Positionen aus anderem Angebot übernehmen" (Wunsch Malermeister Olli):
 * aus dem OFFENEN Angebot heraus einzelne Positionen bereits geschriebener
 * Angebote rosinenpicken — mehrfach nacheinander ("erst 3 aus Kolkwitzweg,
 * dann 2 aus dem anderen"). Der Dialog bleibt deshalb nach jeder Übernahme
 * OFFEN und springt zur Angebots-Liste zurück.
 *
 * Zwei Schritte:
 *   1. Angebot wählen (GET /api/quote-data — Nummer, Kunde, Bauvorhaben,
 *      Datum, Positionsanzahl; Suchfeld, weil Olli nach "Kolkwitz" sucht)
 *   2. Positionen ankreuzen (GET /api/quote-data/:slot — OZ, Kurztext, Menge,
 *      Einheit, EP, GP; Gliederungszeilen erkennbar, nicht wählbar)
 *
 * Die eigentliche Abbildung Quellposition → Zielposition lebt testbar in
 * lib/quote-position-copy.ts (was mitwandert / was nicht, inkl. Begründung).
 */
import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useOfferStore } from '@/lib/store';
import {
  formatiereDatum,
  istKopierbar,
  kopiereQuoteItemAusAngebot,
} from '@/lib/quote-position-copy';
import type { QuoteItem } from '@shared/schema';
import type { QuoteDataPackage } from '@/lib/quote-data-service';
import { formatCurrency } from '@/lib/utils';
import { ArrowLeft, CheckCircle, CopyPlus, Loader2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface QuoteListEntry {
  slot: number;
  customerName?: string;
  projectName?: string;
  offerNumber?: string;
  timestamp?: string;
  itemCount?: number;
}

type Schritt =
  | { stufe: 'liste' }
  | { stufe: 'positionen'; slot: number; paket: QuoteDataPackage };

export default function QuotePositionImportDialog() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [schritt, setSchritt] = useState<Schritt>({ stufe: 'liste' });
  const [quotes, setQuotes] = useState<QuoteListEntry[]>([]);
  const [suche, setSuche] = useState('');
  const [ladeListe, setLadeListe] = useState(false);
  const [ladeSlot, setLadeSlot] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [ausgewaehlt, setAusgewaehlt] = useState<Set<number>>(new Set());
  // "Zuletzt übernommen: 3 Positionen aus AN-…" — der rote Faden über mehrere
  // Übernahmen hinweg, damit Olli sieht, was schon im Ziel gelandet ist.
  const [uebernommen, setUebernommen] = useState<Array<{ anzahl: number; angebotNr: string }>>([]);

  const currentQuoteSlot = useOfferStore((s) => s.currentQuoteSlot);
  const appendPosition = useOfferStore((s) => s.appendPosition);

  const ladeAngebotsListe = async () => {
    setLadeListe(true);
    setFehler(null);
    try {
      const response = await fetch('/api/quote-data');
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || `HTTP ${response.status}`);
      }
      const files: QuoteListEntry[] = result.data?.files || [];
      // Das gerade offene Angebot ist keine Quelle: der Server-Stand kann
      // hinter dem ungespeicherten Editor-Stand liegen (stiller Alt-Stand).
      setQuotes(files.filter((f) => f.slot !== currentQuoteSlot));
    } catch (error) {
      setFehler(error instanceof Error ? error.message : 'Angebotsliste konnte nicht geladen werden');
    } finally {
      setLadeListe(false);
    }
  };

  const oeffneDialog = () => {
    setIsOpen(true);
    setSchritt({ stufe: 'liste' });
    setSuche('');
    setAusgewaehlt(new Set());
    setUebernommen([]);
    void ladeAngebotsListe();
  };

  const waehleAngebot = async (slot: number) => {
    setLadeSlot(slot);
    setFehler(null);
    try {
      const response = await fetch(`/api/quote-data/${slot}`);
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || `HTTP ${response.status}`);
      }
      setSchritt({ stufe: 'positionen', slot, paket: result.data as QuoteDataPackage });
      setAusgewaehlt(new Set());
    } catch (error) {
      setFehler(error instanceof Error ? error.message : `Angebot aus Slot ${slot} konnte nicht geladen werden`);
    } finally {
      setLadeSlot(null);
    }
  };

  const gefilterteQuotes = useMemo(() => {
    const term = suche.trim().toLowerCase();
    if (!term) return quotes;
    return quotes.filter((q) =>
      (q.offerNumber || '').toLowerCase().includes(term) ||
      (q.customerName || '').toLowerCase().includes(term) ||
      (q.projectName || '').toLowerCase().includes(term),
    );
  }, [quotes, suche]);

  const quellItems: QuoteItem[] = schritt.stufe === 'positionen' ? (schritt.paket.items || []) : [];
  const kopierbareItems = useMemo(() => quellItems.filter(istKopierbar), [quellItems]);
  const alleGewaehlt = kopierbareItems.length > 0 && kopierbareItems.every((i) => ausgewaehlt.has(i.id));

  const toggleAlle = () => {
    if (alleGewaehlt) {
      setAusgewaehlt(new Set());
    } else {
      setAusgewaehlt(new Set(kopierbareItems.map((i) => i.id)));
    }
  };

  const toggleItem = (id: number) => {
    setAusgewaehlt((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const uebernehmeAuswahl = () => {
    if (schritt.stufe !== 'positionen' || ausgewaehlt.size === 0) return;
    const { paket, slot } = schritt;
    const quelle = {
      angebotNr: paket.header?.angebot_nr || paket.metadata?.offerNumber || `Slot ${slot}`,
      datum: paket.header?.datum,
      kunde: paket.header?.kunde || paket.metadata?.customerName,
      slot,
    };

    // In Quell-Reihenfolge übernehmen; IDs fortlaufend ab der höchsten
    // belegten ID des Ziels (Positionen UND quoteItems, Muster addNewRow).
    let anzahl = 0;
    for (const item of quellItems) {
      if (!istKopierbar(item) || !ausgewaehlt.has(item.id)) continue;
      const state = useOfferStore.getState();
      const nextId = Math.max(
        0,
        ...state.positions.map((p) => p.id),
        ...state.quoteItems.map((i) => i.id),
      ) + 1;
      const paar = kopiereQuoteItemAusAngebot(item, { id: nextId, quelle });
      appendPosition(paar.position, paar.quoteItem);
      anzahl += 1;
    }

    setUebernommen((prev) => [...prev, { anzahl, angebotNr: quelle.angebotNr }]);
    toast({
      title: 'Positionen übernommen',
      description: `${anzahl} ${anzahl === 1 ? 'Position' : 'Positionen'} aus Angebot ${quelle.angebotNr} eingefügt — Preise sind der Stand vom ${formatiereDatum(quelle.datum) || 'unbekannten Datum'}.`,
    });

    // Dialog bleibt OFFEN und springt zur Liste zurück — Ollis "und aus dem
    // anderen nochmal zwei" ist der Normalfall, nicht die Ausnahme.
    setSchritt({ stufe: 'liste' });
    setAusgewaehlt(new Set());
  };

  const quellHeader = schritt.stufe === 'positionen' ? schritt.paket.header : null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={oeffneDialog}
        className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
        data-testid="quote-import-open"
        title="Einzelne Positionen aus bereits geschriebenen Angeboten in dieses Angebot kopieren"
      >
        <CopyPlus className="h-4 w-4" />
        Aus anderem Angebot
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CopyPlus className="h-5 w-5 text-blue-600" />
              Positionen aus anderem Angebot übernehmen
            </DialogTitle>
            <DialogDescription>
              Angebot wählen, Positionen ankreuzen, übernehmen — danach gern direkt
              das nächste Angebot. Übernommene Preise sind der Stand des Quell-Angebots
              und werden im Ziel entsprechend markiert.
            </DialogDescription>
          </DialogHeader>

          {uebernommen.length > 0 && (
            <div
              className="rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 flex items-center gap-2"
              data-testid="quote-import-summary"
            >
              <CheckCircle className="h-4 w-4 flex-shrink-0" />
              <span>
                In dieser Sitzung übernommen:{' '}
                {uebernommen.map((u, i) => (
                  <span key={i}>
                    {i > 0 && ', '}
                    {u.anzahl} aus {u.angebotNr}
                  </span>
                ))}
              </span>
            </div>
          )}

          {fehler && (
            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900">
              {fehler}
            </div>
          )}

          {schritt.stufe === 'liste' ? (
            <div className="flex flex-col gap-3 min-h-0 flex-1">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  value={suche}
                  onChange={(e) => setSuche(e.target.value)}
                  placeholder="Suchen nach Angebotsnummer, Kunde oder Bauvorhaben (z. B. Kolkwitz)…"
                  className="pl-8"
                  data-testid="quote-import-search"
                />
              </div>

              <div className="flex-1 overflow-y-auto border rounded-md">
                {ladeListe ? (
                  <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Angebote werden geladen…
                  </div>
                ) : gefilterteQuotes.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-500">
                    {quotes.length === 0
                      ? 'Keine anderen gespeicherten Angebote vorhanden.'
                      : 'Kein Angebot passt zur Suche.'}
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left text-xs text-gray-600">
                        <th className="py-2 px-3">Angebots-Nr.</th>
                        <th className="py-2 px-3">Kunde</th>
                        <th className="py-2 px-3">Bauvorhaben</th>
                        <th className="py-2 px-3">Gespeichert</th>
                        <th className="py-2 px-3 text-right">Positionen</th>
                        <th className="py-2 px-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {gefilterteQuotes.map((quote) => (
                        <tr
                          key={quote.slot}
                          className="border-t hover:bg-blue-50/50 cursor-pointer"
                          onClick={() => void waehleAngebot(quote.slot)}
                          data-testid={`quote-import-source-${quote.slot}`}
                        >
                          <td className="py-2 px-3 font-medium">{quote.offerNumber || `Slot ${quote.slot}`}</td>
                          <td className="py-2 px-3">{quote.customerName || '—'}</td>
                          <td className="py-2 px-3">{quote.projectName || '—'}</td>
                          <td className="py-2 px-3 whitespace-nowrap">{formatiereDatum(quote.timestamp) || '—'}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{quote.itemCount ?? '—'}</td>
                          <td className="py-2 px-3 text-right">
                            {ladeSlot === quote.slot ? (
                              <Loader2 className="h-4 w-4 animate-spin inline-block text-blue-600" />
                            ) : (
                              <span className="text-blue-600 text-xs">Positionen ansehen →</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setIsOpen(false)} data-testid="quote-import-done">
                  Fertig
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="flex flex-col gap-3 min-h-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="text-sm">
                  <div className="font-medium">
                    Angebot {quellHeader?.angebot_nr || schritt.paket.metadata?.offerNumber || `Slot ${schritt.slot}`}
                    {quellHeader?.datum && (
                      <span className="ml-2 text-amber-700 font-normal">
                        Preisstand vom {formatiereDatum(quellHeader.datum)}
                      </span>
                    )}
                  </div>
                  <div className="text-gray-600">
                    {[quellHeader?.kunde, quellHeader?.bauvorhaben].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setSchritt({ stufe: 'liste' }); setAusgewaehlt(new Set()); }}
                  className="flex items-center gap-1 text-gray-600"
                  data-testid="quote-import-back"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Anderes Angebot
                </Button>
              </div>

              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                Die Preise stammen aus diesem älteren Angebot. Übernommene Positionen werden im
                Ziel mit ihrer Herkunft markiert — der Vermerk verschwindet, sobald ein Preis
                manuell geändert wird.
              </div>

              <div className="flex-1 overflow-y-auto border rounded-md">
                {kopierbareItems.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-500">
                    Dieses Angebot enthält keine übernehmbaren Positionen.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left text-xs text-gray-600">
                        <th className="py-2 px-3 w-[36px]">
                          <Checkbox
                            checked={alleGewaehlt}
                            onCheckedChange={toggleAlle}
                            aria-label="Alle Positionen auswählen"
                            data-testid="quote-import-select-all"
                          />
                        </th>
                        <th className="py-2 px-3 w-[80px]">OZ</th>
                        <th className="py-2 px-3">Kurztext</th>
                        <th className="py-2 px-3 text-right w-[80px]">Menge</th>
                        <th className="py-2 px-3 w-[70px]">Einheit</th>
                        <th className="py-2 px-3 text-right w-[100px]">EP</th>
                        <th className="py-2 px-3 text-right w-[110px]">GP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quellItems.map((item) => {
                        if (!istKopierbar(item)) {
                          // Gliederungszeile: erkennbar, aber nicht wählbar.
                          return (
                            <tr key={item.id} className="border-t bg-gray-100/70">
                              <td className="py-1.5 px-3" />
                              <td className="py-1.5 px-3 text-xs text-gray-500">{item.positionNumber || ''}</td>
                              <td colSpan={5} className="py-1.5 px-3 font-medium text-gray-700">
                                {item.name || item.beschreibung}
                                <span className="ml-2 text-[10px] uppercase tracking-wide text-gray-400">Titel</span>
                              </td>
                            </tr>
                          );
                        }
                        const gewaehlt = ausgewaehlt.has(item.id);
                        const gp = item.preisFehlt ? null : item.preis * item.menge;
                        return (
                          <tr
                            key={item.id}
                            className={`border-t cursor-pointer ${gewaehlt ? 'bg-blue-50/60' : 'hover:bg-gray-50'}`}
                            onClick={() => toggleItem(item.id)}
                            data-testid={`quote-import-row-${item.id}`}
                          >
                            <td className="py-1.5 px-3" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={gewaehlt}
                                onCheckedChange={() => toggleItem(item.id)}
                                aria-label={`Position ${item.positionNumber || item.name || item.id} auswählen`}
                              />
                            </td>
                            <td className="py-1.5 px-3 text-xs text-gray-500 whitespace-nowrap">{item.positionNumber || '—'}</td>
                            <td className="py-1.5 px-3">
                              <div className="font-medium">{item.name || item.beschreibung}</div>
                              {item.name && item.beschreibung && item.beschreibung !== item.name && (
                                <div className="text-xs text-gray-500 line-clamp-1">{item.beschreibung}</div>
                              )}
                              {(item.isBedarfsposition || item.isAlternativposition) && (
                                <Badge variant="outline" className="mt-0.5 px-1 py-0 text-[10px] text-amber-700 border-amber-300">
                                  {item.isBedarfsposition ? 'Bedarf/Eventual' : 'Alternativ'}
                                </Badge>
                              )}
                            </td>
                            <td className="py-1.5 px-3 text-right tabular-nums">{item.menge}</td>
                            <td className="py-1.5 px-3">{item.einheit}</td>
                            <td className="py-1.5 px-3 text-right tabular-nums">
                              {item.preisFehlt ? '–' : formatCurrency(item.preis)}
                            </td>
                            <td className="py-1.5 px-3 text-right tabular-nums">
                              {gp === null ? '–' : formatCurrency(gp)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              <DialogFooter className="items-center gap-2">
                <span className="text-sm text-gray-600 mr-auto">
                  {ausgewaehlt.size} von {kopierbareItems.length} Positionen ausgewählt
                </span>
                <Button
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                  data-testid="quote-import-done-2"
                >
                  Fertig
                </Button>
                <Button
                  onClick={uebernehmeAuswahl}
                  disabled={ausgewaehlt.size === 0}
                  data-testid="quote-import-apply"
                >
                  {ausgewaehlt.size === 0
                    ? 'Übernehmen'
                    : `${ausgewaehlt.size} ${ausgewaehlt.size === 1 ? 'Position' : 'Positionen'} übernehmen`}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
